import React, { useState, useEffect } from 'react';
import { propOr, pathOr, isEmpty, compose, find, prop, values, isNil } from 'ramda';
import { useQuery, useMutation } from '@apollo/react-hooks';
import Loader from 'react-loader-spinner';
import { UPDATE_EMAIL_TEMPLATE, CREATE_EMAIL_TEMPLATE, SEND_TEST_EMAIL } from '../apollo/mutation';
import { GET_EMAIL_TEMPLATE, GET_CURRENT_SUBSCRIPTION } from '../apollo/queries';
import {
  Form,
  Page,
  Card,
  Button,
  FormLayout,
  Select,
  Stack,
  TextField,
  AppProvider,
  Frame,
  TextContainer,
  Spinner,
  Badge,
  Icon,
  Link,
  Toast,
  Layout,
  Modal,
  InlineError,
} from '@shopify/polaris';
import { LockMinor } from '@shopify/polaris-icons';
import moment from 'moment';
import enTranslations from '@shopify/polaris/locales/en.json';
import { TitleBar } from '@shopify/app-bridge-react';
import { Editor } from '@tinymce/tinymce-react';

import { onSubmitFailed } from '../../utils/errors';

const isNilOrEmpty = (value) => isNil(value) || isEmpty(value);
const defaultTemplateVariables = [
  'candidate.name',
  'candidate.email',
  'candidate.phone',
  'candidate.created_at',
  'candidate.last_name',
  'candidate.source',
  'job.title',
  'job.category',
  'profile.company_name',
  'sender.first_name',
  'sender.last_name',
  'sender.email',
];

const EmailTemplateSetup = (props) => {
  const node = document.getElementsByClassName('shop_container');
  const dataProps = JSON.parse(node[0].getAttribute('data-react-props'));
  const { shopifyToken = dataProps.shop.shopify_token } = props;
  const [isLoading, setLoading] = useState(false);
  const [disabled, setDisabled] = useState(true);
  const [testModal, setTestModal] = useState(false);
  const [testTarget, setTestTarget] = useState('');
  const [archiveModal, setArchiveModal] = useState(false);
  const [toastStatus, setToastStatus] = useState({ status: false, message: '' });
  const [formErrors, setFormErrors] = useState({
    subject: false,
    body: false,
  });

  const toggleTestModal = () => setTestModal(!testModal);
  const toggleArchiveModal = () => setArchiveModal(!archiveModal);
  const [updateEmailTemplate] = useMutation(UPDATE_EMAIL_TEMPLATE);
  const [createEmailTemplate] = useMutation(CREATE_EMAIL_TEMPLATE);
  const [sendTestEmail] = useMutation(SEND_TEST_EMAIL);

  let { history } = props;
  const [form, setState] = useState({
    title: '',
    subject: '',
    body: '',
    sender: '',
    senderEmail: '',
  });

  const {
    data: dataR,
    loading: loadingR,
    refetch: refetchR,
  } = useQuery(GET_EMAIL_TEMPLATE, {
    variables: { shopifyToken, emailTemplateId: props.match.params.id },
  });

  const { data: subscriptionData } = useQuery(GET_CURRENT_SUBSCRIPTION, {
    variables: { shopifyToken },
  });

  const changeRoute = prop('push')(history);
  const emailTemplate = propOr({}, 'emailTemplate')(dataR);

  const { shop = {} } = { ...subscriptionData };
  const { currentSubscription = {}, teamMembers = [] } = { ...shop };
  const isSubscribedUser = !isNilOrEmpty(currentSubscription);

  const allAccess =
    true ||
    (isSubscribedUser &&
      (currentSubscription.subscriptionType === 'Premium' ||
        currentSubscription.subscriptionType === 'Growth' ||
        currentSubscription.subscriptionType === 'Business'));

  if (props.match.params.id) {
    useEffect(() => {
      setState({
        ...form,
        title: emailTemplate.title,
        body: emailTemplate.body,
        subject: emailTemplate.subject,
        sender: emailTemplate.sender,
        senderEmail: emailTemplate.senderEmail,
      });
      refetchR();
    }, [emailTemplate]);
  }

  useEffect(() => {
    if (!allAccess !== disabled) {
      setDisabled(!allAccess);
    }
  }, [allAccess]);

  const updateField = (name) => (value) => {
    setFormErrors({ ...formErrors, [name]: false });

    setState({
      ...form,
      [name]: value,
    });
  };

  const handleEditorChange = (content) => {
    setState({
      ...form,
      body: content,
    });
  };

  const onUpdateStatus = async (status) => {
    setLoading(true);
    setArchiveModal(false);

    try {
      const response = await updateEmailTemplate({
        variables: {
          ...emailTemplate,
          status,
          shopifyToken,
          emailTemplateId: props.match.params.id,
        },
      });
      const errors = pathOr([], ['data', 'updateEmailTemplate', 'errors'])(response);
      const emptyErrors = isEmpty(errors);
      setLoading(false);
      if (emptyErrors) {
        setToastStatus({
          status: true,
          message: `The email template was updated successfully.`,
        });
        refetchR();
      } else {
        console.log(errors);
        setToastStatus({
          status: true,
          message: 'There is an error with the server',
        });
      }
    } catch (err) {
      console.log(err);
      setLoading(false);
      setToastStatus({
        status: true,
        message: 'There is an error with the server',
      });
    }
  };

  const updateEmailTemplateForm = async (duplicate = false) => {
    try {
      const updatedErrors = duplicate
        ? {
            title: compose(isEmpty, prop('title'))(emailTemplate),
            body: compose(isEmpty, prop('body'))(emailTemplate),
            subject: compose(isEmpty, prop('subject'))(emailTemplate),
          }
        : {
            title: compose(isEmpty, prop('title'))(form),
            body: compose(isEmpty, prop('body'))(form),
            subject: compose(isEmpty, prop('subject'))(form),
          };

      setFormErrors(updatedErrors);

      if (
        compose(
          find((item) => item),
          values
        )(updatedErrors)
      ) {
        setTimeout(() => {
          onSubmitFailed();
        }, 10);
      } else {
        setLoading(true);

        let variables = {
          body: form.body,
          title: form.title,
          subject: form.subject,
          sender: form.sender,
          status: form.status || 'active',
          senderEmail: form.senderEmail,
          shopifyToken,
        };

        let templateMutation = createEmailTemplate;

        if (props.match.params.id) {
          variables = {
            ...variables,
            title: emailTemplate.group === 'user' ? form.title : emailTemplate?.title,
            status: emailTemplate.status || 'active',
            emailTemplateId: props.match.params.id,
          };
          templateMutation = updateEmailTemplate;
        }

        if (duplicate) {
          templateMutation = createEmailTemplate;
          variables = {
            ...emailTemplate,
            title: `Copy of ${emailTemplate?.title}`,
            shopifyToken,
          };
        }

        const response = await templateMutation({ variables });
        const errors = pathOr(
          [],
          ['data', duplicate || !props.match.params.id ? 'createEmailTemplate' : 'updateEmailTemplate', 'errors']
        )(response);
        const emptyErrors = isEmpty(errors);

        if (emptyErrors) {
          setToastStatus({
            status: true,
            message: `The email template was ${props.match.params.id && !duplicate ? 'updated' : 'created'} successfully.`,
          });
          if (props.match.params.id && !duplicate) {
            refetchR();
            history.push('/settings/templates/email');
          } else {
            const { data: responseData = {} } = { ...response };
            const { createEmailTemplate: createTemplateData = {} } = { ...responseData };
            const { emailTemplate: created = {} } = { ...createTemplateData };
            setLoading(false);
            history.push(`/edit-email-template/${created?.id}`);
          }
        } else {
          console.log(errors);
          setLoading(false);
          setToastStatus({ status: true, message: 'There is an error with the server' });
        }
      }
    } catch (err) {
      console.log(err);
      setLoading(false);
      setToastStatus({ status: true, message: 'There is an error with the server' });
    }
  };

  if (loadingR || isLoading) {
    return (
      <div className='loader-container'>
        <Loader type='TailSpin' color='#6371c7' height={80} width={80} />
      </div>
    );
  }

  const renderButton = isLoading ? (
    <TextContainer>
      <Spinner accessibilityLabel='Loading form field' size='small' />

      <p> Publishing your page </p>
    </TextContainer>
  ) : props.match.params.id ? (
    <Button primary={true} onClick={() => updateEmailTemplateForm()} size='large' disabled={!allAccess}>
      Update
    </Button>
  ) : (
    <Button primary={true} submit={true} size='large' disabled={!allAccess}>
      Save
    </Button>
  );

  const onSubmit = () => {
    const updatedErrors = {
      title: compose(isEmpty, prop('title'))(form),
      body: compose(isEmpty, prop('body'))(form),
      subject: compose(isEmpty, prop('subject'))(form),
    };

    setFormErrors(updatedErrors);

    if (
      compose(
        find((item) => item),
        values
      )(updatedErrors)
    ) {
      setTimeout(() => {
        onSubmitFailed();
      }, 10);
    } else {
      // handleFormSubmit();
      updateEmailTemplateForm();
    }
  };

  const sendTestEmailSuccess = () => {
    setLoading(false);
    setToastStatus({ status: true, message: 'Test email was sent successfully' });
    toggleTestModal();
  };

  const sendTestEmailFailure = () => {
    setLoading(false);
    setToastStatus({ status: true, message: 'There is an error with the server' });
  };

  const onSendEmail = () => {
    setLoading(true);

    sendTestEmail({
      variables: {
        shopifyToken,
        message: form.body,
        subject: form.subject,
        from: form.senderEmail || testTarget,
        to: testTarget,
      },
    })
      .then(sendTestEmailSuccess)
      .catch(sendTestEmailFailure);
  };

  const archiveActionModalSection = () => {
    if (emailTemplate.status === 'active') {
      var text = "Archiving this email template will hide it from your email templates list and the email template dropdown when contacting a candidate.";
        if (emailTemplate.title === 'Disqualify Email') {
          text += "Candidates you disqualify will no longer be automatically emailed."
      }
    } else {
      var text = "Unarchiving this email template will show it in your email templates list and the email template dropdown when contacting a candidate.";
    }
    return <Modal.Section><p>{text}</p></Modal.Section>;

  }

  return (
    <AppProvider i18n={enTranslations}>
      <Frame>
        <TitleBar title='Email Templates' breadcrumbs={[{ content: 'Settings', onAction: () => { history.push('/settings'); } }]} />
        <Page
        breadcrumbs={[{ content: 'All email templates', onAction: () => { history.push('/settings/templates/email'); } }]}
          title={props.match.params.id ? emailTemplate.title : 'Create email template'}
          titleMetadata={
            props.match.params.id ? (
              <Badge status={emailTemplate?.status === 'active' ? 'success' : 'default'}>
                <span className={'capitalize'}>{emailTemplate?.status}</span>
              </Badge>
            ) : null
          }
          subtitle={props.match.params.id ? `Updated ${moment(emailTemplate?.updatedAt).format('MMMM D, YYYY')}` : ''}
          primaryAction={{
            content: props.match.params.id ? 'Update' : 'Save',
            onAction: () => updateEmailTemplateForm(),
            disabled: !allAccess,
          }}
          secondaryActions={
            allAccess
              ? [
                  {
                    content: 'Duplicate',
                    disabled: !props.match.params.id,
                    onAction: () => updateEmailTemplateForm(true),
                  },
                  {
                    content: 'Send test email',
                    disabled: !form?.title || !form?.body || !form?.subject,
                    onAction: toggleTestModal,
                  },
                ]
              : undefined
          }>
          {toastStatus.status && (
            <Toast content={toastStatus.message} onDismiss={() => setToastStatus({ status: false, message: '' })} />
          )}
          <div id='email-template-container'>
            <Form onSubmit={() => onSubmit()}>
              <hr className='hr' />
              <br />
              <Layout.Section style={{ display: 'flex' }}>
                <Layout.Section>
                  <Card sectioned>
                    {!allAccess && (
                      <>
                        <Stack spacing={'extraTight'}>
                          <Icon source={LockMinor} />
                          <Link onClick={() => changeRoute('/account-plan')} removeUnderline>
                            Upgrade account plan
                          </Link>
                        </Stack>
                        <br />
                      </>
                    )}
                    <FormLayout>
                      {(!props.match.params.id || emailTemplate.group === 'user') && (
                        <FormLayout.Group>
                          <div>
                            <TextField
                              id='title'
                              label='Title'
                              value={form.title}
                              name='title'
                              disabled={disabled}
                              onChange={updateField('title')}
                            />
                            {formErrors.title && <InlineError message='Title is required' fieldID='title' />}
                          </div>
                        </FormLayout.Group>
                      )}
                      <FormLayout.Group>
                        <div>
                          <TextField
                            id='subject'
                            label='Subject'
                            value={form.subject}
                            name='subject'
                            onChange={updateField('subject')}
                            disabled={disabled}
                          />
                          {formErrors.subject && <InlineError message='Subject is required' fieldID='subject' />}
                        </div>
                      </FormLayout.Group>

                      <FormLayout.Group>
                        <div>
                          <TextField
                            id='sender'
                            label='Sender name'
                            value={form.sender}
                            name='sender'
                            onChange={updateField('sender')}
                            disabled={disabled}
                          />
                        </div>
                      </FormLayout.Group>

                      <FormLayout.Group>
                        <div>
                          <TextField
                            id='senderEmail'
                            label='Sender Email'
                            value="jobs@shophire.co"
                            disabled
                            style={{ 'backgroundColor': '#e9f5ff' }}
                          />
                        </div>
                      </FormLayout.Group>
                      <FormLayout.Group>
                        <div>
                          <TextField
                            id='replyToEmail'
                            label='Reply-to Email'
                            value={form.senderEmail}
                            name='senderEmail'
                            onChange={updateField('senderEmail')}
                            disabled={disabled}
                          />
                        </div>
                      </FormLayout.Group>
                      <div>
                        <div className='Polaris-Labelled__LabelWrapper'>
                          <div className='Polaris-Label'>
                            <label id='subjectLabel' for='body' className='Polaris-Label__Text'>
                              Body
                            </label>
                          </div>
                        </div>
                        {disabled && (
                          <Editor
                            value={form.body}
                            disabled={disabled}
                            initialValue={form.body || ''}
                            init={{
                              height: 510,
                              menubar: false,
                              plugins: ['advlist autolink lists link', 'code fullscreen', 'wordcount'],
                              toolbar:
                                'undo redo | formatselect | fontselect | fontsizeselect | bold italic forecolor | \
                        alignleft aligncenter alignright alignjustify | \
                        bullist numlist outdent indent | removeformat | help',
                            }}
                            id='body'
                            name='body'
                            apiKey='czdl8uo4qtlzlp7ozdpws89rify9vokuyuvw21w1jr8n394z'
                            onEditorChange={handleEditorChange}
                          />
                        )}
                        {!disabled && (
                          <Editor
                            value={form.body}
                            initialValue={form.body || ''}
                            init={{
                              height: 510,
                              menubar: false,
                              plugins: ['advlist autolink lists link', 'code fullscreen', 'wordcount'],
                              toolbar:
                                'undo redo | formatselect | fontselect | fontsizeselect | bold italic forecolor | \
                        alignleft aligncenter alignright alignjustify | \
                        bullist numlist outdent indent | removeformat | help',
                            }}
                            id='body'
                            name='body'
                            apiKey='czdl8uo4qtlzlp7ozdpws89rify9vokuyuvw21w1jr8n394z'
                            onEditorChange={handleEditorChange}
                          />
                        )}
                        {formErrors.body && <InlineError message='Email body is required' fieldID='body' />}
                      </div>
                    </FormLayout>
                  </Card>
                </Layout.Section>
                <Layout.Section secondary>
                  <Card title='Template variables' sectioned>
                    <ul style={{ marginTop: '-5px' }}>
                      {(emailTemplate.variables || defaultTemplateVariables).map((value) => (
                        <li>
                          <p> {`{{${value}}}`} </p>
                          <br />
                        </li>
                      ))}
                    </ul>
                  </Card>
                </Layout.Section>
              </Layout.Section>
              <br />
              <hr className='hr' />
              <br />
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <div style={{ display: 'flex', flexDirection: 'row' }}>
                  <div style={{ marginRight: '10px' }}>
                    <Button onClick={() => history.push('/settings/templates/email')} size='large'>
                      Cancel
                    </Button>
                  </div>
                  {(emailTemplate.group === 'user' || emailTemplate.group === 'external') && (
                    <Button
                      destructive={emailTemplate.status === 'active'}
                      outline
                      onClick={toggleArchiveModal}
                      size='large'
                      disabled={!allAccess}>
                      {emailTemplate.status === 'active' ? 'Archive' : 'Unarchive'} template
                    </Button>
                  )}
                </div>
                {renderButton}
              </div>
            </Form>
          </div>
        </Page>
        <Modal
          open={!!testModal}
          onClose={toggleTestModal}
          title={'Test Email'}
          primaryAction={{
            content: 'Send test email',
            onAction: onSendEmail,
            disabled: !testTarget,
          }}
          secondaryActions={[
            {
              content: 'Cancel',
              onAction: toggleTestModal,
            },
          ]}
          loading={false}>
          <Modal.Section>
            <FormLayout>
              <FormLayout.Group>
                <Select
                  label='Team member'
                  options={(teamMembers || []).map((member) => {
                    const { firstName, lastName, email } = { ...member };
                    return {
                      value: email,
                      label: `${firstName} ${lastName} <${email}>`,
                    };
                  })}
                  onChange={setTestTarget}
                  value={testTarget}
                  placeholder={'Select a hiring team member'}
                />
              </FormLayout.Group>
            </FormLayout>
          </Modal.Section>
        </Modal>
        <Modal
          open={archiveModal}
          onClose={toggleArchiveModal}
          title={`${emailTemplate.status === 'active' ? 'Archive' : 'Unarchive'} email template`}
          primaryAction={{
            content: `Yes, ${emailTemplate.status === 'archived' ? 'un' : ''}archive`,
            onAction: () => onUpdateStatus(emailTemplate.status === 'active' ? 'archived' : 'active'),
          }}
          secondaryActions={[
            {
              content: 'Cancel',
              onAction: toggleArchiveModal,
            },
          ]}
          loading={false}>
          {archiveActionModalSection()}
        </Modal>
      </Frame>
    </AppProvider>
  );
};

export default EmailTemplateSetup;
